import React, { useEffect, useState } from 'react'
import { chunk } from 'lodash'
import {
  Autocomplete,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  MenuItem,
  TextField,
} from '@mui/material'
import { toast } from 'react-toastify'
import { Link } from 'react-router-dom'
import PhoneInput from 'react-phone-input-2'
import 'react-phone-input-2/lib/style.css'
import { allowedCountries } from './wizard/sections/form-completion/CompletionPhone'
import { useQuery } from 'react-query'
import {
  facebookService,
  leadCardService,
  useGetAllBusinesses,
  useGetBusiness,
} from '@marketing-milk/frontend'
import {
  Business,
  FacebookLeadgenButtonType,
  formatInternationalPhoneNumber,
  isBusinessWebsite,
  LeadCard,
  normalizeCallPhoneNumber,
  ShareLeadCardOverrides,
  ShareLeadCardResult,
  SHARE_TARGET_LIMIT,
  ShareLinkMode,
  SharePhoneOverride,
} from '@marketing-milk/interfaces'

// What a share that stopped partway could and couldn't be sure of, so nobody retries into duplicates.
interface Interruption {
  unsure: Business[] // the batch in flight when the connection dropped: copies may exist
  notSent: Business[] // refused before any copy, or never reached
  reason?: string
}

const BusinessList = ({ label, businesses }: { label: string; businesses: Business[] }) => (
  <ul className="list-disc pl-5 mt-0 mb-2" aria-label={label}>
    {businesses.map(_ => (
      <li key={_.id}>{_.businessName}</li>
    ))}
  </ul>
)

interface ShareLeadCardModalProps {
  leadCard: LeadCard
  onClose: () => void
}

interface LinkChoice {
  mode: ShareLinkMode
  url: string
}

// Meta rejects a link without its protocol, so the dialog holds the share until one is there
// rather than letting every business fail the same way.
const isFullURL = (url: string) => /^https?:\/\/[^\s/]+\.[^\s]+$/i.test(url.trim())
const linkOverride = ({ mode, url }: LinkChoice) =>
  mode === 'custom' ? { mode, url: url.trim() } : { mode }

const phoneModeLabels: Record<SharePhoneOverride['mode'], string> = {
  source: "This form's number",
  page_phone: "Each business's Page phone",
  custom: 'Fixed number',
}

const modeLabels: Record<ShareLinkMode, string> = {
  source: "This form's link",
  business_website: "Each business's website",
  custom: 'Fixed URL',
}

// Long enough to show the spread of websites a share will use, short enough to keep the dialog
// scannable; the rest is one click away.
const PREVIEW_COUNT = 3

interface TargetPreviewProps {
  label: string
  targets: Business[]
  emptyText: string
  renderValue: (target: Business) => React.ReactNode
}

// Shows what an "each business's …" choice becomes for the businesses picked, so the size of the
// change is visible before anything is sent.
function TargetPreview({ label, targets, emptyText, renderValue }: TargetPreviewProps) {
  const [showAll, setShowAll] = useState(false)

  if (targets.length === 0) {
    return <p className="text-sm text-gray-500 m-0">{emptyText}</p>
  }

  const shown = showAll ? targets : targets.slice(0, PREVIEW_COUNT)
  const hidden = targets.length - PREVIEW_COUNT
  return (
    <div>
      <ul className="list-none p-0 m-0 text-sm" aria-label={`${label} for each business`}>
        {shown.map(target => (
          <li key={target.id} className="break-all">
            <span className="font-semibold">{target.businessName}:</span> {renderValue(target)}
          </li>
        ))}
      </ul>
      {hidden > 0 && (
        <Button size="small" className="px-0" onClick={() => setShowAll(_ => !_)}>
          {showAll ? 'Show fewer' : `Show ${hidden} more`}
        </Button>
      )}
    </div>
  )
}

const missing = (text: string) => <span className="text-amber-700">{text}</span>

// Pages keep their phone on Meta, so each row asks for its own, only once it is on screen. Keyed
// as the Call Now field keys it, so a Page looked up there isn't asked again.
function PagePhone({ businessID }: { businessID: number }) {
  const { data, error, isLoading } = useQuery(
    ['facebook-page-phone', String(businessID)],
    () => facebookService.getFacebookPagePhone(businessID),
    { staleTime: Infinity, retry: false }
  )
  if (isLoading) return <span className="text-gray-500">checking…</span>
  if (error) return missing("couldn't check the Page")
  return data ? (
    <>{formatInternationalPhoneNumber(data)}</>
  ) : (
    missing("no phone listed, won't get a copy")
  )
}

interface LinkFieldProps {
  id: string
  label: string
  choice: LinkChoice
  onChange: (choice: LinkChoice) => void
  sourceValue: string
  targets: Business[]
  allowWebsite?: boolean
  disabled: boolean
}

// The select names the choice; the right side shows what it sends. The menu carries the source's
// full link too, so it can be read while choosing.
function LinkField({
  id,
  label,
  choice,
  onChange,
  sourceValue,
  targets,
  allowWebsite = true,
  disabled,
}: LinkFieldProps) {
  const showError = choice.mode === 'custom' && !!choice.url.trim() && !isFullURL(choice.url)
  return (
    <div className="flex flex-col gap-2 sm:flex-row sm:items-start">
      <TextField
        select
        id={id}
        label={label}
        size="small"
        className="sm:w-2/5 shrink-0"
        value={choice.mode}
        disabled={disabled}
        SelectProps={{
          renderValue: value => modeLabels[value as ShareLinkMode],
          MenuProps: { PaperProps: { style: { maxWidth: 420 } } },
        }}
        onChange={e => onChange({ ...choice, mode: e.target.value as ShareLinkMode })}
      >
        <MenuItem value="source">
          <span className="flex flex-col whitespace-normal">
            <span>{modeLabels.source}</span>
            <span className="text-xs text-gray-500 break-all">{sourceValue}</span>
          </span>
        </MenuItem>
        {allowWebsite && (
          <MenuItem value="business_website">{modeLabels.business_website}</MenuItem>
        )}
        <MenuItem value="custom">{modeLabels.custom}</MenuItem>
      </TextField>
      {/* Text is nudged to sit level with the select's value; an input is the select's height already. */}
      <div className={`sm:w-3/5 min-w-0 ${choice.mode === 'custom' ? '' : 'sm:pt-2'}`}>
        {choice.mode === 'custom' ? (
          <TextField
            id={`${id}-url`}
            label={`${label} URL`}
            size="small"
            fullWidth
            placeholder="https://"
            value={choice.url}
            disabled={disabled}
            error={showError}
            helperText={showError ? 'Include http:// or https://' : undefined}
            onChange={e => onChange({ ...choice, url: e.target.value })}
          />
        ) : choice.mode === 'business_website' ? (
          <TargetPreview
            label={label}
            targets={targets}
            emptyText="Pick businesses to see the websites they'll use."
            renderValue={target =>
              target.website?.trim() || missing("no website set, won't get a copy")
            }
          />
        ) : (
          <p className="text-sm break-all m-0" aria-label={`${label} sent`}>
            {sourceValue}
          </p>
        )}
      </div>
    </div>
  )
}

// Sharing is a one-time copy: Meta lead forms can't be edited once created, so each business gets
// its own form that nothing links back to this one, and nobody can fix a copy afterwards. The
// links and phone are the parts that usually belong to one business, so they are chosen here, once
// for the whole share, with "each business's …" filled in per business.
export function ShareLeadCardModal({ leadCard, onClose }: ShareLeadCardModalProps) {
  const business = useGetBusiness()
  const otherBusinesses = useGetAllBusinesses().filter(_ => _.id !== business.id)
  const [targets, setTargets] = useState<Business[]>([])
  const [isSharing, setIsSharing] = useState(false)
  const [results, setResults] = useState<ShareLeadCardResult[]>()
  const [interruption, setInterruption] = useState<Interruption>()
  const [progress, setProgress] = useState<{ done: number; total: number }>()
  const withoutPage = targets.filter(_ => !_.fbPageID)

  // A share bigger than one request goes out batch by batch from this page, so leaving mid-way
  // strands the rest.
  useEffect(() => {
    if (!isSharing) return
    const warn = (e: BeforeUnloadEvent) => {
      e.preventDefault()
      e.returnValue = ''
    }
    window.addEventListener('beforeunload', warn)
    return () => window.removeEventListener('beforeunload', warn)
  }, [isSharing])

  const thankYouPage = leadCard.thankYouPage
  const callsBusiness = thankYouPage?.callToAction === FacebookLeadgenButtonType.CALL_BUSINESS
  const hasThankYouLink = !!thankYouPage && !callsBusiness

  const [privacyLink, setPrivacyLink] = useState<LinkChoice>({ mode: 'source', url: '' })
  const [thankYouLink, setThankYouLink] = useState<LinkChoice>({
    mode: isBusinessWebsite(thankYouPage?.link, business.website) ? 'business_website' : 'source',
    url: '',
  })
  const [phone, setPhone] = useState<SharePhoneOverride['mode']>('page_phone')
  // E.164, or '' while unset — as the Call Now field holds it.
  const [fixedPhone, setFixedPhone] = useState('')
  const fixedPhoneE164 = normalizeCallPhoneNumber(fixedPhone)
  const sourcePhone =
    formatInternationalPhoneNumber(thankYouPage?.phoneNumber) ?? thankYouPage?.phoneNumber

  const choicesInUse = [privacyLink, ...(hasThankYouLink ? [thankYouLink] : [])]
  const choicesAreValid =
    choicesInUse.every(_ => _.mode !== 'custom' || isFullURL(_.url)) &&
    !(callsBusiness && phone === 'custom' && !fixedPhoneE164)

  const nameOf = (businessID: number) =>
    otherBusinesses.find(_ => _.id === businessID)?.businessName ?? `Business ${businessID}`

  async function share() {
    const overrides: ShareLeadCardOverrides = {
      privacyLink:
        privacyLink.mode === 'custom'
          ? { mode: 'custom', url: privacyLink.url.trim() }
          : { mode: 'source' },
      ...(hasThankYouLink && { thankYouLink: linkOverride(thankYouLink) }),
      ...(callsBusiness && {
        thankYouPhone:
          phone === 'custom' ? { mode: phone, number: fixedPhoneE164 } : { mode: phone },
      }),
    }

    // The server takes SHARE_TARGET_LIMIT businesses a request so each answers inside the load
    // balancer's timeout; a bigger share goes out in batches, one after another.
    const batches = chunk(targets, SHARE_TARGET_LIMIT)
    const shared: ShareLeadCardResult[] = []
    let stopped: Interruption | undefined

    setIsSharing(true)
    setProgress({ done: 0, total: targets.length })
    try {
      for (let index = 0; index < batches.length; index++) {
        const batch = batches[index]
        try {
          shared.push(
            ...(await leadCardService.share(
              business.id,
              leadCard.id,
              batch.map(_ => _.id),
              overrides
            ))
          )
          setProgress({ done: shared.length, total: targets.length })
        } catch (e: any) {
          const status: number | undefined = e?.response?.status
          const message: string | undefined = e?.response?.data?.message
          // A 4xx is the server refusing the request before any copy is made. Anything else — a
          // timeout, a dropped connection, a 5xx — may have cut it off with copies already made.
          const refused = !!status && status >= 400 && status < 500
          if (refused && shared.length === 0) {
            toast.error(message ?? 'Could not share this lead card.')
            return
          }
          stopped = {
            unsure: refused ? [] : batch,
            notSent: [...(refused ? batch : []), ...batches.slice(index + 1).flat()],
            reason: refused ? message : undefined,
          }
          break
        }
      }

      setResults(shared)
      setInterruption(stopped)
      const failed = shared.filter(_ => _.status === 'failed').length
      if (stopped) {
        toast.warn('Sharing stopped partway. Check the list before sharing again.')
      } else if (failed === 0) {
        toast.success(
          `${leadCard.formName} shared to ${shared.length} business${
            shared.length === 1 ? '' : 'es'
          }.`
        )
      } else {
        toast.warn(`${failed} of ${shared.length} could not be shared.`)
      }
    } finally {
      setIsSharing(false)
      setProgress(undefined)
    }
  }

  return (
    <Dialog open onClose={isSharing ? undefined : onClose} fullWidth maxWidth="md">
      <DialogTitle>Share “{leadCard.formName}”</DialogTitle>
      <DialogContent>
        {results ? (
          <ul className="list-none p-0 m-0" aria-label="Share results">
            {results.map(result => (
              <li key={result.businessID} className="py-1">
                {result.status === 'shared' ? (
                  <>
                    <span className="text-green-700 font-semibold">Shared</span> to{' '}
                    {nameOf(result.businessID)}
                    {' · '}
                    {/* A new tab, so the rest of the results stay up. */}
                    <Link
                      to={`/${result.businessID}/lead-cards/${result.leadCard.id}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-mm-primary-600 hover:underline"
                    >
                      View lead card
                    </Link>
                  </>
                ) : (
                  <>
                    <span className="text-red-700 font-semibold">Failed</span> for{' '}
                    {nameOf(result.businessID)}: {result.error}
                  </>
                )}
              </li>
            ))}
            {interruption && (
              <li className="mt-4 text-sm" role="alert">
                <p className="font-semibold text-amber-800 mb-1">Sharing stopped partway.</p>
                {interruption.unsure.length > 0 && (
                  <>
                    <p className="mb-1">
                      The connection dropped while sharing to these, so they may already have a
                      copy. Check their lead cards before sharing to them again:
                    </p>
                    <BusinessList label="May already be shared" businesses={interruption.unsure} />
                  </>
                )}
                {interruption.notSent.length > 0 && (
                  <>
                    <p className="mb-1">
                      Not shared{interruption.reason ? ` (${interruption.reason})` : ''}. Nothing
                      was sent for these:
                    </p>
                    <BusinessList label="Not shared" businesses={interruption.notSent} />
                  </>
                )}
              </li>
            )}
          </ul>
        ) : (
          <>
            <DialogContentText className="mb-4">
              Each business gets its own copy on its Facebook Page, named “{leadCard.formName} -
              Shared” plus the time (UTC). Copies can't be edited afterwards and don't stay in sync
              with this form.
            </DialogContentText>

            <Autocomplete
              multiple
              id="lead-card-share-targets"
              options={otherBusinesses}
              value={targets}
              onChange={(_, selected) => setTargets(selected)}
              getOptionLabel={_ => _.businessName}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              filterSelectedOptions
              disabled={isSharing}
              renderTags={(selected, getTagProps) =>
                selected.map((target, index) => (
                  <Chip
                    {...getTagProps({ index })}
                    key={target.id}
                    size="small"
                    label={target.businessName}
                    color={target.fbPageID ? 'default' : 'warning'}
                    variant={target.fbPageID ? 'filled' : 'outlined'}
                  />
                ))
              }
              renderInput={params => (
                <TextField
                  {...params}
                  variant="outlined"
                  size="small"
                  label="Businesses to share to"
                  placeholder="Search businesses…"
                />
              )}
            />
            {withoutPage.length > 0 && (
              <p className="text-sm text-amber-700 mt-2 mb-0" role="status">
                No Facebook Page connected, so {withoutPage.length === 1 ? 'this' : 'these'} won't
                get a copy: {withoutPage.map(_ => _.businessName).join(', ')}.
              </p>
            )}
            {targets.length > SHARE_TARGET_LIMIT && (
              <p className="text-sm text-gray-600 mt-2 mb-0">
                Shared {SHARE_TARGET_LIMIT} at a time. Keep this page open until it finishes.
              </p>
            )}

            <p className="text-sm font-semibold text-gray-700 mt-4 mb-1">
              For every business in this share
            </p>
            <div className="flex flex-col gap-3 pt-2" aria-label="Share overrides" role="group">
              <LinkField
                id="share-privacy-link"
                label="Privacy policy link"
                choice={privacyLink}
                onChange={setPrivacyLink}
                sourceValue={leadCard.privacy.link}
                targets={targets}
                allowWebsite={false}
                disabled={isSharing}
              />
              {hasThankYouLink && (
                <LinkField
                  id="share-thank-you-link"
                  label="Thank-you link"
                  choice={thankYouLink}
                  onChange={setThankYouLink}
                  sourceValue={thankYouPage?.link ?? ''}
                  targets={targets}
                  disabled={isSharing}
                />
              )}
              {callsBusiness && (
                <div className="flex flex-col gap-2 sm:flex-row sm:items-start">
                  <TextField
                    select
                    id="share-thank-you-phone"
                    label="Thank-you phone"
                    size="small"
                    className="sm:w-2/5 shrink-0"
                    value={phone}
                    disabled={isSharing}
                    SelectProps={{
                      renderValue: value => phoneModeLabels[value as SharePhoneOverride['mode']],
                    }}
                    onChange={e => setPhone(e.target.value as SharePhoneOverride['mode'])}
                  >
                    <MenuItem value="source">
                      <span className="flex flex-col">
                        <span>{phoneModeLabels.source}</span>
                        <span className="text-xs text-gray-500">{sourcePhone}</span>
                      </span>
                    </MenuItem>
                    <MenuItem value="page_phone">{phoneModeLabels.page_phone}</MenuItem>
                    <MenuItem value="custom">{phoneModeLabels.custom}</MenuItem>
                  </TextField>
                  <div
                    className={`sm:w-3/5 min-w-0 text-sm ${phone === 'custom' ? '' : 'sm:pt-2'}`}
                  >
                    {phone === 'source' ? (
                      <p className="m-0" aria-label="Thank-you phone sent">
                        {sourcePhone}
                      </p>
                    ) : phone === 'custom' ? (
                      <>
                        <PhoneInput
                          value={fixedPhone.replace(/^\+/, '')}
                          country="us"
                          onlyCountries={allowedCountries}
                          countryCodeEditable={false}
                          disabled={isSharing}
                          onChange={digits => setFixedPhone(digits ? `+${digits}` : '')}
                          inputProps={{ 'aria-label': 'Thank-you phone number' }}
                          // Matches the small MUI fields beside it.
                          inputStyle={{ width: '100%', height: 40 }}
                          isValid={!!fixedPhoneE164 || !fixedPhone}
                        />
                        {fixedPhone && !fixedPhoneE164 && (
                          <p className="text-xs text-red-700 mt-1 mb-0">
                            That isn't a valid phone number.
                          </p>
                        )}
                      </>
                    ) : (
                      <>
                        <TargetPreview
                          label="Thank-you phone"
                          targets={targets}
                          emptyText="Pick businesses to see their Page phones."
                          renderValue={target => <PagePhone businessID={target.id} />}
                        />
                        <p className="text-xs text-gray-500 mt-1 mb-0">
                          Read again from each Page when shared. A business whose Page lists no
                          phone won't get a copy.
                        </p>
                      </>
                    )}
                  </div>
                </div>
              )}
            </div>
            {(leadCard.pagePhoto || leadCard.imageURL) && (
              <p className="text-sm text-gray-600 mt-3 mb-0">The cover image isn't copied.</p>
            )}
            {progress && (
              <p className="text-sm font-semibold text-gray-700 mt-3 mb-0" role="status">
                Shared {progress.done} of {progress.total}. Keep this page open until it finishes.
              </p>
            )}
          </>
        )}
      </DialogContent>
      <DialogActions>
        {results ? (
          <Button variant="contained" onClick={onClose}>
            Done
          </Button>
        ) : (
          <>
            <Button onClick={onClose} disabled={isSharing}>
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={share}
              disabled={isSharing || targets.length === 0 || !choicesAreValid}
              startIcon={isSharing ? <CircularProgress size={16} color="inherit" /> : undefined}
            >
              {isSharing
                ? `Sharing ${progress?.done ?? 0} of ${progress?.total ?? targets.length}…`
                : 'Share'}
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  )
}
